import React from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import styles from "./InputFieldsController.module.css";
import {
  FieldProps,
  hasRequiredValidation,
  getLabelTextStyle,
} from "./InputFieldsController.utils";
import { ArrowRepeat, ArrowRight, InfoCircleFill } from "react-bootstrap-icons";

interface LabeledInputWrapperProps {
  field: FieldProps & {
    handleSelect?: (id: string) => void; // This is to pass the handle select functionality for labels
    showSourceToggle?: boolean;
    onSourceReset?: () => void;
    showEmitButton?: boolean;
    onEmitClick?: () => void;
  };
  children: React.ReactNode;
}

/**
 * LabeledInputWrapper provides consistent label positioning for all field types.
 * Supports top, left, right, and bottom label positions.
 */
const LabeledInputWrapper: React.FC<LabeledInputWrapperProps> = ({
  field,
  children,
}) => {
  const isRequired = hasRequiredValidation(field);
  const labelPosition = field.labelPosition || "top";

  const wrapperClass =
    labelPosition === "left"
      ? styles.labelLeftWrapper
      : labelPosition === "right"
      ? styles.labelRightWrapper
      : labelPosition === "bottom"
      ? styles.labelBottomWrapper
      : styles.labelTopWrapper;

  return (
    <div className={wrapperClass}>
      {field.label && (
        <div className="d-flex align-items-center justify-content-between">
          <div className={styles.fieldLabelContent}>
            <Form.Label
              htmlFor={field.id}
              style={getLabelTextStyle(field.textStyle)}
              className={styles.fieldLabel}
              onClick={(e) => {
                e.stopPropagation(); // prevent bubbling to avoid double triggers
                field.handleSelect?.(field.id); // safe optional chaining
              }}
            >
              <span className={styles.fieldLabelText}>{field.label}</span>
              {isRequired && (
                <span style={{ color: "red", marginLeft: 5 }}>*</span>
              )}
            </Form.Label>
            {field.systemStatus === "unmapped" && (
              <OverlayTrigger
                trigger={["hover"]}
                placement="top"
                delay={{ show: 120, hide: 80 }}
                overlay={
                  <Tooltip
                    id={`unmapped-tooltip-${field.id}`}
                    className={styles.fieldStatusTooltip}
                  >
                    {field.onResolveSystemField ? "Not in system - click to resolve" : "Not in system"}
                  </Tooltip>
                }
              >
                <span
                  className={styles.fieldStatusIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    field.onResolveSystemField?.(field.id);
                  }}
                  title={field.onResolveSystemField ? "Resolve field" : "Not in system"}
                >
                  <InfoCircleFill />
                </span>
              </OverlayTrigger>
            )}
          </div>
          <div className="d-flex align-items-center gap-1">
            {field.showEmitButton && field.onEmitClick && (
              <button
                type="button"
                title={field.emitButtonLabel || "Emit"}
                className={styles.refreshFieldButton}
                onClick={(e) => {
                  e.stopPropagation();
                  field.onEmitClick?.();
                }}
              >
                <ArrowRight size={16} />
              </button>
            )}

            {!field.typeahead && field.showSourceToggle && field.onSourceReset && (
              <button
                type="button"
                title="Refresh"
                className={styles.refreshFieldButton}
                onClick={(e) => {
                  e.stopPropagation();
                  field.onSourceReset?.();
                }}
              >
                <ArrowRepeat size={16} />
              </button>
            )}
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

export default LabeledInputWrapper;
